'use client';

import { useTranslations } from 'next-intl';
import { usePathname } from '@/i18n/routing';

interface PageHeaderProps {
  title: string;
  breadcrumb?: Array<{
    label: string;
    href?: string;
  }>;
  className?: string;
}

export default function PageHeader({ 
  title, 
  breadcrumb = [], 
  className = '' 
}: PageHeaderProps) {
  const t = useTranslations();
  const pathname = usePathname();

  // Default breadcrumb if none provided
  const defaultBreadcrumb = [
    { label: t('Navigation.home'), href: `/${pathname?.split('/')[1] || 'en'}` }
  ];

  const finalBreadcrumb = breadcrumb.length > 0 ? breadcrumb : defaultBreadcrumb;

  return (
    <section 
      className={`relative flex items-center justify-center px-4 sm:px-6 lg:px-8 font-space-grotesk bg-dark-navy ${className}`}
    >
      {/* Responsive height: mobile 250px, tablet 350px, desktop 440px */}
      <div className="max-w-7xl mx-auto w-full h-[350px] sm:h-[400px] md:h-[450px] lg:h-[500px] xl:h-[600px] flex items-center justify-center">
        <div className="text-center px-4">
          {/* Main Title - Responsive font sizes */}
          <h1 
            className="font-bold text-white mb-4 sm:mb-5 md:mb-6 font-space-grotesk text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[74px] leading-tight"
          >
            {title}
          </h1>
          
          {/* Breadcrumb Navigation - Responsive spacing and font sizes */}
          <nav className="flex items-center justify-center flex-wrap gap-2 sm:gap-0" aria-label="Breadcrumb">
            {finalBreadcrumb.map((item, index) => (
              <div key={index} className="flex items-center">
                {index > 0 && (
                  <svg 
                    width="8" 
                    height="13" 
                    viewBox="0 0 8 13" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    className="rtl:rotate-180 mx-2 sm:mx-3 md:mx-4 lg:mx-[19px] flex-shrink-0"
                  >
                    <path 
                      d="M1.5 13C1.21875 13 0.96875 12.9062 0.78125 12.7188C0.375 12.3438 0.375 11.6875 0.78125 11.3125L5.0625 7L0.78125 2.71875C0.375 2.34375 0.375 1.6875 0.78125 1.3125C1.15625 0.90625 1.8125 0.90625 2.1875 1.3125L7.1875 6.3125C7.59375 6.6875 7.59375 7.34375 7.1875 7.71875L2.1875 12.7188C2 12.9062 1.75 13 1.5 13Z" 
                      fill="white"
                    />
                  </svg>
                )}
                {item.href ? (
                  <a
                    href={item.href}
                    className="text-white hover:opacity-80 transition-opacity duration-200 font-space-grotesk text-sm sm:text-base md:text-lg lg:text-xl xl:text-[22px] font-bold leading-relaxed"
                    style={{
                      color: 'var(--White, #FFF)',
                      fontFeatureSettings: "'liga' off, 'clig' off"
                    }}
                  >
                    {item.label}
                  </a>
                ) : (
                  <span 
                    className="text-white font-space-grotesk text-sm sm:text-base md:text-lg lg:text-xl xl:text-[22px] font-bold leading-relaxed"
                    style={{
                      color: 'var(--White, #FFF)',
                      fontFeatureSettings: "'liga' off, 'clig' off"
                    }}
                  >
                    {item.label}
                  </span>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>
    </section>
  );
}
